import TimeWheelPicker from 'components/ui/time-wheel-picker';
import Tooltip from 'components/common/Tooltip';
import { CUSTOM_ROUTINE_TRIGGER, TIME_VALIDATION_FIELDS } from 'enums';
import { CUSTOM_ROUTINE_FEATURE, EMPTY_STRING } from 'constants/general';
import { TIME_WHEEL_PICKER_REMOUNT_KEY } from 'constants/time-wheel-picker';
import { t } from 'services/i18n.service';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { updateCustomRoutine } from 'store/reducer/setting/slice';
import { validateCustomRoutineTimeIntervals } from 'utils/validators';
import { CustomRoutineContext } from '..';
import Card from 'components/ui/Card';
import { PropsWithChildren } from 'react';
import { routineTimeBoundariesSelector } from 'store/reducer/setting/selectors';
import { getTimeWheelPickerInitialValue } from 'utils/helpers';
import { DEFAULT_ROUTINE_TIME_BOUNDARIES } from 'assets/data';

const RoutineTimeBoundaries = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch();
  const { isSettingLoading, activeTabIndex } = useAppSelectorShallow(
    routineTimeBoundariesSelector
  );

  const customRoutine = useContext(CustomRoutineContext);

  const isScheduledModeEnabled = useMemo(
    () => customRoutine?.trigger === CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE,
    [customRoutine]
  );
  const [errorMessages, setErrorMessages] = useState<{
    startTime: string[];
    endTime: string[];
  }>({
    startTime: [],
    endTime: []
  });

  const isValidSchedule = useMemo(
    () => validateCustomRoutineTimeIntervals(customRoutine),
    [customRoutine]
  );

  useEffect(() => {
    const { errors } = isValidSchedule;
    (errors.startTime.length || errors.endTime.length) &&
      setErrorMessages(errors);
  }, [isValidSchedule]);

  const handleValidation = useCallback(
    (feature: string) => {
      if (customRoutine?.trigger === CUSTOM_ROUTINE_TRIGGER.ON_SCHEDULE) {
        const { isInvalidStartTime, isInvalidEndTime } = isValidSchedule;
        return feature === TIME_VALIDATION_FIELDS.START_TIME
          ? isInvalidStartTime
          : isInvalidEndTime;
      } else {
        return true;
      }
    },
    [isValidSchedule, customRoutine?.trigger]
  );

  const pickerRemountSuffix = isSettingLoading
    ? TIME_WHEEL_PICKER_REMOUNT_KEY.LOADING
    : TIME_WHEEL_PICKER_REMOUNT_KEY.READY;

  const handleTimeChange = useCallback(
    (
      value: string | undefined,
      field: 'start_time' | 'end_time',
      feature: (typeof CUSTOM_ROUTINE_FEATURE)[keyof typeof CUSTOM_ROUTINE_FEATURE]
    ) => {
      const next = value || EMPTY_STRING;
      const current = customRoutine?.[field] || EMPTY_STRING;
      if (!customRoutine || !next || next === current) return;

      dispatch(
        updateCustomRoutine({
          feature,
          position: customRoutine.position,
          value: next
        })
      );
    },
    [customRoutine, dispatch]
  );

  return (
    <Card
      variant='outlined'
      className={`~gap-x-5/10 ~gap-y-2/4 flex-wrap animate-fadeIn ${
        isScheduledModeEnabled ? 'flex justify-center items-center' : 'hidden'
      }`}
      data-testid='routine-time-boundaries'>
      <div className='flex flex-col gap-2 py-2'>
        <label className='~text-[0.65rem]/xs flex items-center gap-1'>
          {t('start_time')}
          <Tooltip
            message={t('tooltip_custom_routine_schedule_start_time', {
              custom_routine_name: customRoutine?.name ?? ''
            })}
            iconStyles='w-4 h-4 inline-flex ml-1 mb-0.5'
          />
        </label>
        <TimeWheelPicker
          key={`start-${customRoutine?.id}-${customRoutine?.trigger}-${activeTabIndex}-${pickerRemountSuffix}`}
          initialValue={getTimeWheelPickerInitialValue(
            customRoutine?.start_time,
            DEFAULT_ROUTINE_TIME_BOUNDARIES.START_TIME
          )}
          onChange={(value) =>
            handleTimeChange(
              value,
              TIME_VALIDATION_FIELDS.START_TIME,
              CUSTOM_ROUTINE_FEATURE.START_TIME
            )
          }
          size='small'
          isLoading={isSettingLoading}
        />
        {errorMessages.startTime.length &&
        handleValidation(TIME_VALIDATION_FIELDS.START_TIME) ? (
          <ul className='relative mt-auto'>
            {errorMessages.startTime.map((error) => (
              <li key={error} className='w-fit text-[0.7rem] text-red-600 py-1'>
                {error}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className='flex flex-col gap-2 py-2'>
        <label className='~text-[0.65rem]/xs flex items-center gap-1'>
          {t('end_time')}
          <Tooltip
            message={t('tooltip_custom_routine_schedule_end_time', {
              custom_routine_name: customRoutine?.name ?? ''
            })}
            iconStyles='w-4 h-4 inline-flex ml-1 mb-0.5'
          />
        </label>
        <TimeWheelPicker
          key={`end-${customRoutine?.id}-${customRoutine?.trigger}-${activeTabIndex}-${pickerRemountSuffix}`}
          initialValue={getTimeWheelPickerInitialValue(
            customRoutine?.end_time,
            DEFAULT_ROUTINE_TIME_BOUNDARIES.END_TIME
          )}
          onChange={(value) =>
            handleTimeChange(
              value,
              TIME_VALIDATION_FIELDS.END_TIME,
              CUSTOM_ROUTINE_FEATURE.END_TIME
            )
          }
          size='small'
          isLoading={isSettingLoading}
        />
        {errorMessages.endTime.length &&
        handleValidation(TIME_VALIDATION_FIELDS.END_TIME) ? (
          <ul className='relative mt-auto'>
            {errorMessages.endTime.map((error) => (
              <li key={error} className='w-fit text-[0.7rem] text-red-600 py-1'>
                {error}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {children}
    </Card>
  );
};

export default RoutineTimeBoundaries;
